// Builds and downloads an RFC 5545 (.ics) file for one appointment, entirely
// client-side — no server round-trip, nothing leaves the browser except the
// file the user explicitly saves.
//
// Shared, not events-specific: it takes a neutral payload rather than an events row, so
// anything with a wall-clock start can offer a calendar file. Each app supplies a small
// adapter that maps its own columns onto this shape (apps/events/helpers/ics.js,
// apps/recruitment/helpers/ics.js).
//
//   { uid, title, start, end, location, description, url, filenamePart }
//
// `start`/`end` MUST be wall-clock strings ('YYYY-MM-DDTHH:MM:SS', no Z) — see the
// toIcsFloatingDateTime comment below for why, and note that a provider item's own
// `start`/`end` on the landing page are NOT those: they're .toISOString()'d instants.
//
// Security note: title/description/location are free-text fields any
// authenticated user can set. iCalendar's KEY:VALUE text format uses
// unescaped backslash/comma/semicolon/newlines as structural delimiters, so
// every such field MUST be escaped per RFC 5545 §3.3.11 before being written
// into the file — otherwise a crafted title could break out of its field and
// inject extra bogus properties (or even a fake component) into the file.
// `url` is not re-validated here since it's already constrained server-side
// to http(s):// only.

const escapeIcsText = (str) => (str || '')
  .replace(/\\/g, '\\\\')
  .replace(/;/g, '\\;')
  .replace(/,/g, '\\,')
  .replace(/\r\n|\r|\n/g, '\\n')

// DTSTAMP is a genuine UTC instant ("when was this file produced"), so it keeps its Z.
const toIcsUtcDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
}

// DTSTART/DTEND are NOT instants. Start/end arrive as wall-clock text
// ('YYYY-MM-DDTHH:MM:SS') — see events.v_event's and recruitment.v_candidate_interview's
// headers — so the faithful encoding is RFC 5545 form 1, a "floating" DATE-TIME: no Z, no
// TZID. The digits ARE the local time and carry no zone claim, which is exactly what the
// column means.
//
// Deliberately NOT `new Date(...)` + getUTC*(): that re-parses the naive string as local and
// then shifts it. A previous version did that with a Z suffix, which mislabelled the
// wall-clock digits as UTC and rendered a 09:00 event as 11:00 in any Stockholm calendar.
//
// Floating rather than TZID=Europe/Stockholm because TZID without an accompanying VTIMEZONE
// is non-conformant, and a correct Stockholm VTIMEZONE means hand-written STANDARD/DAYLIGHT
// blocks with RRULEs that rot if the EU ever changes its DST rules. Google Calendar and
// Outlook both read a floating DATE-TIME as the importing user's local time, and every viewer
// here is in Stockholm. If non-Stockholm viewers ever matter, TZID + VTIMEZONE is the upgrade.
const toIcsFloatingDateTime = (wallClock) => String(wallClock).replace(/[-:]/g, '').slice(0, 15)

export const buildIcsContent = (appointment) => {
  const stamp = toIcsUtcDateTime(new Date())
  const start = toIcsFloatingDateTime(appointment.start)
  const end = toIcsFloatingDateTime(appointment.end || appointment.start)

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Kayenta Apps//Events//SV',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${escapeIcsText(appointment.uid)}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcsText(appointment.title)}`,
  ]

  if (appointment.location)    lines.push(`LOCATION:${escapeIcsText(appointment.location)}`)
  if (appointment.description) lines.push(`DESCRIPTION:${escapeIcsText(appointment.description)}`)
  if (appointment.url)         lines.push(`URL:${escapeIcsText(appointment.url)}`)

  lines.push('END:VEVENT', 'END:VCALENDAR')

  return lines.join('\r\n')
}

// Filesystem-safe across Windows/Mac/Linux: strips characters those OSes
// reject outright (< > : " / \ | ? * and control chars), then normalizes
// spaces/dashes to underscores so the result reads as one clean token.
const sanitizeFilenamePart = (str) => (str || '')
  // eslint-disable-next-line no-control-regex
  .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
  .replace(/[\s-]+/g, '_')

export const downloadIcs = (appointment) => {
  const filename = `kayenta_apps_${sanitizeFilenamePart(appointment.filenamePart || 'event')}.ics`
  const content = buildIcsContent(appointment)
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()

  // Deferred, not immediate — revoking the object URL (or removing the
  // link) synchronously right after click() has been observed to race
  // with the browser's own download handling in some cases.
  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 0)

  return filename
}
